import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DirectionsBoatOutlinedIcon from '@mui/icons-material/DirectionsBoatOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { NavLink } from 'react-router-dom';

import { SpaRoutes } from '@spa/constants/routes';
import { useLang } from '@spa/i18n/useLang';
import { formatISOToDMYTime } from '@spa/utils/dateHelpers';
import { formatMinuteDuration } from '@spa/utils/numberFormatter';
import type { LogisticsDeliveryCardProps } from '@spa/types/logistics.types';

const baseLabelClass =
  'mb-1 flex items-center gap-1.5 text-[10px] uppercase leading-none tracking-wide text-primary-text/80';

export default function LogisticsDeliveryCard({
  delivery,
  orderServiceName,
}: LogisticsDeliveryCardProps) {
  const t = useLang();
  const currentLocale = window.BioMarAppSettings?.language;

  const TransportIcon =
    delivery.transport.toLowerCase() === 'boat'
      ? DirectionsBoatOutlinedIcon
      : LocalShippingOutlinedIcon;

  return (
    <article className="flex flex-col rounded-md border border-biomar-border bg-white px-4 pb-4 pt-5">
      <div className="flex flex-row items-start justify-between gap-2">
        <h3 className="m-0 font-semibold leading-tight text-primary-text text-base">
          {delivery.deliveryVehicleName}{' '}
          <span className="block font-normal text-primary-text/80 md:inline">
            {t('logistics.deliveryNumber', {
              id: delivery.orderNumber,
            })}
          </span>
        </h3>

        <NavLink
          to={`${SpaRoutes.COMPANY_ORDERS}/${delivery.orderId}`}
          className="inline-flex items-center justify-start gap-1 self-start font-semibold text-primary-text no-underline md:justify-end text-sm"
        >
          {t('logistics.goToOrderDetails')}
          <ArrowForwardIcon className="text-base" />
        </NavLink>
      </div>

      <div className="mt-8 flex flex-col gap-2 md:mt-3 md:gap-1">
        {delivery.lines.map((line) => (
          <div
            key={`${delivery.orderNumber}-${line.product}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-4 leading-tight text-primary-text md:grid-cols-[minmax(0,1fr)_14rem] text-sm"
          >
            <span>{line.product}</span>

            <span className="whitespace-nowrap text-right">
              {line.oldAmount != null && (
                <span className="mr-3 line-through text-xs">
                  {line.oldAmount} {t('global.cart.unitKg')}
                </span>
              )}
              {line.amount} {t('global.cart.unitKg')}
            </span>
          </div>
        ))}
      </div>

      <div
        className="mt-8 grid grid-cols-[3rem_minmax(0,1fr)_minmax(0,8rem)] gap-3 pt-0
        md:mt-auto md:flex md:flex-row md:items-stretch md:gap-2.5 md:pt-4"
      >
        <div
          className="flex h-18 md:h-13 items-center justify-center rounded-md border
         border-biomar-border text-primary-text  md:w-12 md:shrink-0"
        >
          <TransportIcon fontSize="small" />
        </div>

        <div className="flex h-18 md:h-13 min-w-0 flex-col justify-center rounded-md border border-biomar-border bg-white px-4 py-3 md:px-3 md:py-2">
          <div className={baseLabelClass}>
            <span className="truncate">{t('logistics.arrivalTime')}</span>
          </div>

          <span className="font-semibold text-primary-text text-sm">
            {formatISOToDMYTime(delivery.arrivalTime, currentLocale)}
          </span>
        </div>

        <div className="flex h-18 md:h-13 min-w-0 flex-col justify-center rounded-md border border-biomar-border bg-white px-4 py-3 md:w-full md:max-w-35 md:px-3 md:py-2">
          <div className={baseLabelClass}>
            <span className="truncate">{t('logistics.timeToUnload')}</span>
          </div>

          <span className="font-semibold leading-tight text-primary-text text-sm">
            {formatMinuteDuration(delivery.timeToUnload, currentLocale)}
          </span>
        </div>

        <a
          href={delivery.deliveryProviderDetailsLink}
          target="_blank"
          rel="noreferrer"
          className="col-span-3 inline-flex items-center justify-center gap-3 
          rounded-md border border-primary-text px-3 py-3 font-semibold text-primary-text
          no-underline md:col-auto min-h-0 md:w-auto md:flex-none md:py-2 text-sm"
        >
          {t('logistics.goToOrderInService', {
            service: orderServiceName,
          })}
          <OpenInNewIcon fontSize="small" />
        </a>
      </div>
    </article>
  );
}
